import { layoutConfig } from '@layouts/config'
import { AppContentLayoutNav } from '@layouts/enums'
import { useLayoutConfigStore } from '@layouts/stores/config'

export const openGroups = ref([])

/**
 * Return nav link props to use.
 * @param {Object} link Navigation route object with `to`, `href`, and other properties.
 * @returns {Object} Props for navigation link.
 */
export const getComputedNavLinkToProp = computed(() => link => {
  const { target, rel, to, href, context } = link

  // Return props based on whether `to` is a string or route object.
  return link.to ? {
    target,
    rel,
    to: context !== 0 ? `${to}/${context}` : to,
  } : {
    target,
    rel,
    href,
  }
})

/**
 * Return route name for navigation link.
 * @param {Object|String} link Navigation link object or route name.
 * @param {Object} router Vue Router instance.
 * @returns {String|null} Route name or null if not found.
 */
export const resolveNavLinkRouteName = (link, router) => {
  if (!link.to) return null
  
  return typeof link.to === 'string' ? link.to : router.resolve(link.to).name
}

/**
 * Check if nav-link is active.
 * @param {Object} link Nav-link object.
 * @param {Object} router Vue Router instance.
 * @returns {Boolean} True if the nav-link is active.
 */
export const isNavLinkActive = (link, router) => {
  const matchedRoutes = router.currentRoute.value
  const routeName = resolveNavLinkRouteName(link, router)

  if (!routeName) return false

  // Check if the route or meta matches the resolved route name.
  return matchedRoutes.path === routeName ||
         matchedRoutes.meta.navActiveLink === routeName ||
         (matchedRoutes.path.includes(routeName) && link.context === matchedRoutes.params.id)
}

/**
 * Check if nav group is active.
 * @param {Array} children Group children.
 * @param {Object} router Vue Router instance.
 * @returns {Boolean} True if any child link is active.
 */
export const isNavGroupActive = (children, router) => children.some(child => {
  // Recursively check children if it's a group.
  if ('children' in child) return isNavGroupActive(child.children, router)
  
  return isNavLinkActive(child, router)
})

/**
 * Set `dir` attribute on the document.
 * @param {String} dir 'ltr' | 'rtl'
 */
export const _setDirAttr = dir => {
  if (typeof document !== 'undefined') document.documentElement.setAttribute('dir', dir)
}

/**
 * Return dynamic i18n props based on i18n plugin status.
 * @param {String} key i18n translation key.
 * @param {String} [tag='span'] HTML tag to wrap the translation with.
 * @returns {Object} i18n props or empty object.
 */
export const getDynamicI18nProps = (key, tag = 'span') => {
  if (!layoutConfig.app.i18n.enable) return {}
  
  return { keypath: key, tag, scope: 'global' }
}

/**
 * Switch to vertical navigation on smaller screens and revert on larger screens.
 */
export const switchToVerticalNavOnLtOverlayNavBreakpoint = () => {
  const configStore = useLayoutConfigStore()
  const lgAndUpNav = ref(configStore.appContentLayoutNav)

  // Update `lgAndUpNav` if screen is `lgAndUp`.
  watch(() => configStore.appContentLayoutNav, value => {
    if (!configStore.isLessThanOverlayNavBreakpoint) lgAndUpNav.value = value
  })

  // Handle layout switching based on screen size.
  const shouldChangeContentLayoutNav = refAutoReset(true, 500)

  shouldChangeContentLayoutNav.value = false

  watch(() => configStore.isLessThanOverlayNavBreakpoint, val => {
    if (!val) {
      configStore.appContentLayoutNav = lgAndUpNav.value
    } else {
      if (!shouldChangeContentLayoutNav.value) {
        setTimeout(() => {
          configStore.appContentLayoutNav = AppContentLayoutNav.Vertical
        }, 500)
      } else {
        configStore.appContentLayoutNav = AppContentLayoutNav.Vertical
      }
    }
  }, { immediate: true })
}

/**
 * Convert Hex color to RGB.
 * @param {String} hex Hex color code.
 * @returns {String|null} RGB color string or null if invalid.
 */
export const hexToRgb = hex => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i

  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  
  return result ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}` : null
}

/**
 * Convert RGBA color to Hex color with optional alpha removal.
 * @param {String} rgba RGBA color string.
 * @param {Boolean} [forceRemoveAlpha=false] Whether to remove alpha.
 * @returns {String} Hex color code.
 */
export const rgbaToHex = (rgba, forceRemoveAlpha = false) => {
  return `#${rgba
    .replace(/^rgba?\(|\s+|\)$/g, '') // Extract RGBA/RGB values
    .split(',') // Split values
    .filter((_, index) => !forceRemoveAlpha || index !== 3) // Filter alpha if necessary
    .map(num => parseFloat(num)) // Convert to numbers
    .map((num, index) => index === 3 ? Math.round(num * 255) : num) // Convert alpha to 255
    .map(num => num.toString(16)) // Convert to hex
    .map(str => str.padStart(2, '0')) // Ensure two-digit hex
    .join('')}`
}
